import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const OrderModificationModal = ({ order, onClose, onSave }) => {
  const { t } = useTranslation('wholesaler');

  const [items, setItems] = useState(
    order.items.map(item => ({
      ...item,
      originalQuantity: item.originalQuantity || item.quantity,
      originalStatus: item.status || 'active',
      newQuantity: item.quantity,
      newStatus: item.status || 'active',
      modificationNote: item.modificationNote || ''
    }))
  );

  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleQuantityChange = (itemId, newQuantity) => {
    setItems(items.map(item => 
      item._id === itemId 
        ? { ...item, newQuantity: Math.max(0, parseInt(newQuantity) || 0) }
        : item
    ));
  };

  const handleStatusChange = (itemId, newStatus) => {
    setItems(items.map(item => 
      item._id === itemId 
        ? { ...item, newStatus }
        : item
    ));
  };

  const handleNoteChange = (itemId, note) => {
    setItems(items.map(item => 
      item._id === itemId 
        ? { ...item, modificationNote: note }
        : item
    ));
  };

  const isItemModified = (item) => {
    return item.newQuantity !== item.originalQuantity || 
           item.newStatus !== item.originalStatus ||
           item.modificationNote !== '';
  };

  const getUnitPrice = (item) => {
    return item.unitPrice || item.pricing?.unitPrice || 0;
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    
    const updatedItems = items.map(item => {
      const unitPrice = getUnitPrice(item);
      return {
        _id: item._id,
        productId: item.productId,
        name: item.name,
        sku: item.sku,
        unitPrice: unitPrice,
        quantity: item.newQuantity,
        unit: item.unit,
        totalPrice: unitPrice * item.newQuantity,
        images: item.images || [],
        category: item.category,
        status: item.newStatus,
        originalQuantity: item.quantity !== item.newQuantity ? item.quantity : undefined,
        modificationNote: item.modificationNote
      };
    });

    try {
      await onSave(updatedItems, reason);
    } catch (error) {
      console.error('Save failed:', error);
      setIsSaving(false);
    }
  };

  const calculateNewSubtotal = () => {
    return items
      .filter(item => item.newStatus !== 'removed')
      .reduce((sum, item) => {
        const unitPrice = getUnitPrice(item);
        const quantity = item.newQuantity || 0;
        return sum + (unitPrice * quantity);
      }, 0);
  };

  // Calculate discount-aware total
  const calculateNewTotal = () => {
    const subtotal = calculateNewSubtotal();
    let total = subtotal;
    
    // Apply catalog discount if still eligible
    const catalogDiscount = order.catalogDiscount;
    if (catalogDiscount?.percentage > 0 && catalogDiscount?.minimumOrderAmount) {
      if (subtotal >= catalogDiscount.minimumOrderAmount) {
        total -= subtotal * (catalogDiscount.percentage / 100);
      }
      // If below minimum, discount no longer applies
    } else if (catalogDiscount?.percentage > 0) {
      total -= subtotal * (catalogDiscount.percentage / 100);
    }
    
    // Apply customer discount
    const customerDiscount = order.discount;
    if (customerDiscount?.type !== 'none' && customerDiscount?.percentage > 0) {
      total -= subtotal * (customerDiscount.percentage / 100);
    }
    
    // Add delivery fee if present
    if (order.totals?.deliveryFee > 0) {
      total += order.totals.deliveryFee;
    }
    
    return total;
  };

  // Check if catalog discount eligibility changes after modification
  const getCatalogDiscountStatus = () => {
    const catalogDiscount = order.catalogDiscount;
    if (!catalogDiscount?.percentage || catalogDiscount.percentage <= 0) return null;
    
    const newSubtotal = calculateNewSubtotal();
    const minOrder = catalogDiscount.minimumOrderAmount || 0;
    
    if (minOrder > 0 && newSubtotal < minOrder) {
      return {
        lost: true,
        percentage: catalogDiscount.percentage,
        minimum: minOrder,
        shortfall: minOrder - newSubtotal
      };
    }
    return {
      lost: false,
      percentage: catalogDiscount.percentage,
      amount: newSubtotal * (catalogDiscount.percentage / 100)
    };
  };

  const hasModifications = items.some(item => isItemModified(item));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {t('orders.modify.title', 'Τροποποίηση Παραγγελίας')} #{order.orderNumber}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            ×
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>{t('orders.modify.noteLabel', 'Σημείωση')}:</strong> {t('orders.modify.noteText', 'Μπορείτε να τροποποιήσετε ποσότητες, να σημειώσετε προϊόντα ως εξαντλημένα, ή να αφαιρέσετε προϊόντα. Οι αλλαγές θα είναι ορατές στον πελάτη.')}
            </p>
          </div>

          <div className="space-y-4">
            {items.map((item) => {
              const unitPrice = getUnitPrice(item);
              const itemModified = isItemModified(item);
              const quantityChanged = item.newQuantity !== item.originalQuantity;
              const statusChanged = item.newStatus !== item.originalStatus;
              
              return (
                <div 
                  key={item._id} 
                  className={`border rounded-lg p-4 transition-all ${
                    itemModified ? 'border-orange-400 bg-orange-50' : 'border-gray-200'
                  }`}
                >
                  {itemModified && (
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-500 text-white">
                        {t('orders.modify.modified', 'Τροποποιημένο')}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{item.name}</h3>
                      <p className="text-sm text-gray-600">
                        €{unitPrice.toFixed(2)} {t('orders.modify.per', 'ανά')} {item.unit}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${quantityChanged ? 'text-orange-600' : 'text-gray-800'}`}>
                        €{(unitPrice * (item.newQuantity || 0)).toFixed(2)}
                      </p>
                      {quantityChanged && item.originalQuantity > 0 && (
                        <p className="text-xs text-gray-500 line-through">
                          €{(unitPrice * item.originalQuantity).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('orders.modify.quantity', 'Ποσότητα')} {quantityChanged && <span className="text-orange-600">●</span>}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={item.newQuantity}
                        onChange={(e) => handleQuantityChange(item._id, e.target.value)}
                        disabled={item.newStatus === 'removed'}
                        className={`w-full border rounded-md p-2 ${
                          quantityChanged ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                        }`}
                      />
                      {quantityChanged && (
                        <p className="text-xs text-orange-600 mt-1">
                          {t('orders.modify.original', 'Αρχικό')}: {item.originalQuantity} {item.unit}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('orders.modify.status', 'Κατάσταση')} {statusChanged && <span className="text-orange-600">●</span>}
                      </label>
                      <select
                        value={item.newStatus}
                        onChange={(e) => handleStatusChange(item._id, e.target.value)}
                        className={`w-full border rounded-md p-2 ${
                          statusChanged ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                        }`}
                      >
                        <option value="active">{t('orders.modify.statusActive', 'Ενεργό')}</option>
                        <option value="modified">{t('orders.modify.statusModified', 'Τροποποιημένο')}</option>
                        <option value="out_of_stock">{t('orders.modify.statusOutOfStock', 'Εξαντλημένο')}</option>
                        <option value="removed">{t('orders.modify.statusRemoved', 'Αφαίρεση από Παραγγελία')}</option>
                      </select>
                      {statusChanged && (
                        <p className="text-xs text-orange-600 mt-1">
                          {t('orders.modify.original', 'Αρχικό')}: {item.originalStatus}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orders.modify.noteToCustomer', 'Σημείωση προς Πελάτη')} {item.modificationNote && <span className="text-orange-600">●</span>}
                    </label>
                    <textarea
                      value={item.modificationNote}
                      onChange={(e) => handleNoteChange(item._id, e.target.value)}
                      placeholder={t('orders.modify.notePlaceholder', 'Εξηγήστε γιατί τροποποιήθηκε αυτό το προϊόν...')}
                      className={`w-full border rounded-md p-2 text-sm ${
                        item.modificationNote ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                      }`}
                      rows="2"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {hasModifications && (
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-orange-800 mb-2">
                <span className="flex items-center gap-2">
                  {t('orders.modify.reasonLabel', 'Λόγος Τροποποίησης')}
                  <span className="text-xs font-normal text-orange-600">({t('orders.modify.reasonDesc', 'θα σταλεί στον πελάτη')})</span>
                </span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('orders.modify.reasonPlaceholder', 'π.χ. Κάποια προϊόντα είναι προσωρινά μη διαθέσιμα...')}
                className="w-full border border-orange-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                rows="3"
              />
              {!reason && (
                <p className="text-xs text-orange-600 mt-2">
                  {t('orders.modify.noReasonWarning', 'Αν παραμείνει κενό, το email θα εμφανίζει "Δεν δόθηκε λόγος"')}
                </p>
              )}
            </div>
          )}

          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            {/* Subtotal */}
            <div className="flex justify-between items-center text-sm mb-2">
              <span className="text-gray-600">{t('orders.modify.subtotal', 'Υποσύνολο')}:</span>
              <span className="font-medium">€{calculateNewSubtotal().toFixed(2)}</span>
            </div>

            {/* Catalog Discount */}
            {order.catalogDiscount?.percentage > 0 && (() => {
              const discountStatus = getCatalogDiscountStatus();
              return discountStatus ? (
                <>
                  <div className={`flex justify-between items-center text-sm ${discountStatus.lost ? 'line-through opacity-50' : ''}`}>
                    <span className="text-blue-600 font-medium">
                      {t('orders.modify.catalogDiscount', 'Έκπτωση Καταλόγου')} ({discountStatus.percentage}%):
                    </span>
                    <span className="font-semibold text-blue-600">
                      {discountStatus.lost ? t('orders.modify.discountLost', 'Απώλεια') : `-€${discountStatus.amount?.toFixed(2)}`}
                    </span>
                  </div>
                  {discountStatus.lost && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-2 mt-1 mb-2">
                      <p className="text-xs text-red-700">
                        ⚠️ {t('orders.modify.discountLostWarning', 'Η τροποποίηση θα αφαιρέσει την έκπτωση καταλόγου ({{percentage}}%) γιατί το σύνολο (€{{subtotal}}) είναι κάτω από το ελάχιστο (€{{minimum}}).', {
                          percentage: discountStatus.percentage,
                          subtotal: calculateNewSubtotal().toFixed(2),
                          minimum: discountStatus.minimum.toFixed(2)
                        })}
                      </p>
                    </div>
                  )}
                </>
              ) : null;
            })()}

            {/* Customer Discount */}
            {order.discount?.type !== 'none' && order.discount?.percentage > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-green-600 font-medium">
                  {t('orders.modify.customerDiscount', 'Έκπτωση Πελάτη')} ({order.discount.percentage}%):
                </span>
                <span className="font-semibold text-green-600">
                  -€{(calculateNewSubtotal() * order.discount.percentage / 100).toFixed(2)}
                </span>
              </div>
            )}

            {/* Delivery Fee */}
            {order.totals?.deliveryFee > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">{t('orders.modify.deliveryFee', 'Κόστος Μεταφοράς')}:</span>
                <span className="font-medium">€{order.totals.deliveryFee.toFixed(2)}</span>
              </div>
            )}

            {/* Final Total */}
            <div className="flex justify-between items-center text-lg mt-2 pt-2 border-t border-gray-200">
              <span className="font-semibold">{t('orders.modify.newTotal', 'Νέο Σύνολο')}:</span>
              <span className={`font-bold ${
                calculateNewTotal() !== (order.totals?.total || 0) ? 'text-orange-600' : 'text-blue-600'
              }`}>
                €{calculateNewTotal().toFixed(2)}
              </span>
            </div>
            {calculateNewTotal() !== (order.totals?.total || 0) && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{t('orders.modify.originalTotal', 'Αρχικό σύνολο')}:</span>
                  <span className="text-gray-600 line-through">€{(order.totals?.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="font-medium text-orange-600">{t('orders.modify.difference', 'Διαφορά')}:</span>
                  <span className="font-medium text-orange-600">
                    {calculateNewTotal() > (order.totals?.total || 0) ? '+' : ''}
                    €{(calculateNewTotal() - (order.totals?.total || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel', 'Ακύρωση')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('common.saving', 'Αποθήκευση...')}
              </>
            ) : (
              t('orders.modify.saveChanges', 'Αποθήκευση Αλλαγών')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderModificationModal;